import React, { FC, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { below, media, widthBreakpoints } from 'src/config/breakpoints';
import { MediaQuery } from 'src/common/migration/MediaQuery';
import { ListSkeleton } from 'src/user/courses/ListSkeleton';
import { TextBlockSkeleton } from 'src/user/courses/TextBlockSkeleton';
import { LayoutBox } from 'src/common/components/LayoutBox';
import { ElearningItemHeaderSkeleton } from 'src/common/components/Elearnings/ElearningItemHeaderSkeleton';
import { ElearningItemSpeakerInfo } from 'src/common/components/Elearnings/ElearningItemSpeakerInfo';
import { ElearningRelated } from 'src/common/components/Elearnings/ElearningRelated';
import { UserCourseRecordingPageCertificateSkeleton } from 'src/user/courses/components/UserCourseRecordingPage/UserCourseRecordingPageCertificateSkeleton';
import { ElearningPageHeader } from 'src/public/components/elearning/ElearningPageHeader';
import { CourseFooter } from 'src/common/components/CourseFooter';
import { ElearningContent } from 'src/common/components/ElearningContent';
import { ElearningSection } from 'src/common/components/ElearningSection';
import { LayoutPanelCategories } from 'src/common/components/Layout/LayoutPanelCategories';
import { Layout } from 'src/common/components/Layout';
import { useElearningQuery } from 'src/common/hooks/elearning/useElearningQuery';
import { useResetRedirectUrl } from 'src/common/guards/useResetRedirectUrl';
import { layoutActions } from 'src/state/layout/layoutActions';
import { HomepageRoute } from 'src/public/publicRoutes';
import { useDispatch } from 'react-redux';
import { SectionTitle } from 'src/common/components/SectionTitle';
import { useIsLoggedIn } from 'src/common/hooks/useIsLoggedIn';
import { ElearningLessonsAccordion } from 'src/common/components/Elearnings/ElearningLessonsAccordion';
import { ElearningButtonWidget } from 'src/public/components/elearning/ElearningButtonWidget';
import { useJoinElearningAfterRedirect } from 'src/elearning/hooks/useJoinElearningAfterRedirect';
import { useElearningLessonsQuery } from 'src/common/hooks/elearning/useElearningLessonsQuery';
import { ElearningItemInfo } from 'src/common/components/Elearnings/ElearningItemInfo';
import { RouteComponentProps } from 'src/common/migration/RouteComponentProps';
import { useGatsbyLikeNavigate } from 'src/common/migration/useGatsbyLikeNavigate';
import { VendorGetServerSideProps } from 'vendor/utils/VendorGetServerSideProps';
import { buildUnauthenticatedUserKeyEnhancer } from 'src/common/hooks/useCurrentUserKeyEnhancer';
import { QueryKey } from 'src/config/queryKey';
import { getRelated } from 'src/common/services/related';
import { getElearningById, getElearningLessons } from 'src/common/services/elearning';
import { courseQueryStaleTime } from 'src/config/cache';
import { prepareKeywordsString } from 'src/common/services/seo';
import { userElearningIdParam, UserElearningItemRoute } from 'src/user/routes';
import { isErrorResponse } from 'src/common/models/error';
import { StatusCodes } from 'http-status-codes';
import { buildRedirectUrl } from 'src/lib/router.utils';
import { assertElearningParams } from 'src/common/ssr/elearning';
import { setParams } from 'src/common/utils/routing';
import { coerceInt } from 'src/lib/coerceInt';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  margin-bottom: 5rem;
  margin-top: 2.5rem;

  ${media.w.min.px576(css`
    margin-bottom: 3.75rem;
    padding: 0 0.375rem;
  `)}

  ${media.w.min.px768(css`
    display: grid;
    grid-template-columns: minmax(300px, auto) minmax(150px, 203px);
    grid-template-areas: 'info info' 'left-panel right-panel' 'footer footer';
    gap: 2.5rem;
    margin-bottom: 2.5rem;
    padding: 0 2.25rem 0 0;
  `)}

  ${media.w.min.px992(css`
    margin-top: 2.5rem;
    padding: 0 1.75rem 0 1.125rem;
  `)}
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  grid-area: left-panel;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1.25rem;

  ${media.w.min.px576(css`
    padding: 0 0.375rem;
  `)};

  ${media.w.min.px768(css`
    padding: 0 2.25rem 0 0;
  `)};

  ${media.w.min.px992(css`
    margin-top: 2.5rem;
  `)}
`;

const RightPanel = styled.div`
  display: none;

  ${media.w.min.px768(css`
    display: block;
    grid-area: right-panel;
  `)}
`;

export interface ElearningPageProps extends RouteComponentProps {
  elearningId?: number;
}

export const ElearningPage: FC<ElearningPageProps> = ({ elearningId }) => {
  const navigate = useGatsbyLikeNavigate();
  const dispatch = useDispatch();

  const { data: elearning, isFetching } = useElearningQuery(elearningId);

  const postRedirectJoin = useJoinElearningAfterRedirect(elearning); // has to be before useResetRedirectUrl
  useResetRedirectUrl(!isFetching);

  const isLoggedIn = useIsLoggedIn();

  const { data: lessons, isLoading: isLessonsLoading } = useElearningLessonsQuery(elearning?.id);

  useEffect(() => {
    if (elearning) {
      const primaryCategory =
        elearning.categories.find((category) => category.main) ?? elearning.categories?.[0];

      primaryCategory && dispatch(layoutActions.setActivePanelCategory(String(primaryCategory.id)));
    }

    if (!isFetching && !elearning) {
      navigate(HomepageRoute);
    } else if (!isFetching && isLoggedIn && elearning?.elearningUserEnrollment?.isEnrolled) {
      navigate(setParams(userElearningIdParam)(UserElearningItemRoute)(String(elearningId)));
    }
  }, [isFetching, elearning, isLoggedIn]);

  const seoKeywords = useMemo(() => prepareKeywordsString(elearning?.keywords), [elearning]);

  if (!elearningId) {
    navigate(HomepageRoute);
    return null;
  }

  const isPageLoading = isFetching || postRedirectJoin.isLoading;

  return (
    <Layout
      leftPanel={<LayoutPanelCategories />}
      seoDescription={elearning?.description}
      seoKeywords={seoKeywords}
      title={elearning?.title ?? 'Ładowanie..'}
      widget={!isPageLoading && elearning && <ElearningButtonWidget elearning={elearning} />}
      widgetPadding={58}
      scrollToTopButton
    >
      <>
        <LayoutBox tabletBackground>
          {isPageLoading && <ElearningItemHeaderSkeleton />}

          {!isPageLoading && elearning && <ElearningPageHeader elearning={elearning} />}

          {isPageLoading && <TextBlockSkeleton />}
          {!isPageLoading && elearning && (
            <InfoWrapper>
              <ElearningItemInfo
                categories={elearning.categories}
                courseLevel={elearning.level}
                duration={elearning.duration}
                updateDate={elearning.updateDate}
                materialsQuantity={elearning.materialsQuantity ?? 0}
              />
            </InfoWrapper>
          )}

          <Container>
            <Content>
              <ElearningSection>
                {isPageLoading || isLessonsLoading ? (
                  <ListSkeleton rowCount={5} />
                ) : (
                  lessons && (
                    <>
                      <SectionTitle>Moduły zajęć</SectionTitle>
                      <ElearningLessonsAccordion lessons={lessons} />
                    </>
                  )
                )}
              </ElearningSection>

              <MediaQuery maxWidth={below(widthBreakpoints.px768)}>
                <ElearningSection>
                  {isPageLoading ? (
                    <ListSkeleton rowCount={5} />
                  ) : (
                    elearning && <ElearningItemSpeakerInfo speaker={elearning.speakers[0]} />
                  )}
                </ElearningSection>
              </MediaQuery>

              {elearning && <ElearningContent elearning={elearning} isLoading={isPageLoading} />}
            </Content>

            <MediaQuery minWidth={widthBreakpoints.px768}>
              <RightPanel>
                {isPageLoading ? (
                  <UserCourseRecordingPageCertificateSkeleton />
                ) : (
                  elearning && <ElearningItemSpeakerInfo speaker={elearning.speakers[0]} />
                )}
              </RightPanel>
            </MediaQuery>
          </Container>

          <CourseFooter />
        </LayoutBox>

        <ElearningRelated elearningId={elearningId} />
      </>
    </Layout>
  );
};

export const getServerSideProps = VendorGetServerSideProps(async (ctx) => {
  try {
    const { elearningId: elearningIdParam } = assertElearningParams(ctx.params);

    const elearningId = coerceInt(elearningIdParam);

    const { key: elearningKey } = buildUnauthenticatedUserKeyEnhancer([
      QueryKey.Elearning,
      elearningId,
    ]);
    await ctx.queryClient.fetchQuery(elearningKey, () => getElearningById(elearningId), {
      staleTime: courseQueryStaleTime,
    });

    const { key: lessonsKey } = buildUnauthenticatedUserKeyEnhancer([
      QueryKey.ElearningLessons,
      `${elearningId}`,
    ]);
    await ctx.queryClient.prefetchQuery(lessonsKey, () => getElearningLessons(elearningId, false));

    const { key: relatedKey } = buildUnauthenticatedUserKeyEnhancer([
      QueryKey.RelatedCourses,
      elearningId,
    ]);
    await ctx.queryClient.prefetchQuery(relatedKey, () => getRelated(elearningId, false));

    return {
      props: {},
    };
  } catch (error) {
    if (isErrorResponse(error) && error.status === StatusCodes.NOT_FOUND) {
      return {
        notFound: true,
      };
    }
  }

  return {
    redirect: {
      destination: buildRedirectUrl(HomepageRoute, ctx),
      permanent: false,
    },
  };
});

export default ElearningPage;
